import React, { useMemo, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import CheckCircleIcon from '/public/icons/circle-check.svg';
import CancelIcon from '/public/icons/circle-xmark.svg';
import Image from 'next/image';
import { storeLimit } from '../store/slices/storeLimit';
import { useDispatch, useSelector } from 'react-redux';
import { parseShopifyHost } from '../utils/parseShopifyHost';
import { registerSubscription } from '../store/slices/registerSubscription';
import Layout from '../components/Layout';
import { useStoreLimitPolling } from '../hooks/useStoreLimitPolling';

const Subscription = () => {
  const name = useSelector((state) => state?.storeLimit?.planName);
  const subscriptionExpired = useSelector((state) => state?.storeLimit?.isSubscriptionShow);
  const storeDomain = useSelector(state => state.config?.clientStoreHash);
  const { isShopifyApp, shopName } = parseShopifyHost();
  const subscriptionStatus = useSelector((state) => state?.subscriptionDetails?.status);
  const subscriptionPlan = useSelector((state) => state?.subscriptionDetails?.planName);
  const plansDetails = useSelector((state) => state?.plans?.data);
  const planStatus = useSelector((state) => state?.plans?.status);
  const dispatch = useDispatch(); 
  
  const { getSubscriptionStatus } = useStoreLimitPolling(storeDomain);
  const isLoading = getSubscriptionStatus === 'inprogress';


  // ✅ useMemo ABOVE the early return — fixes React error #310
  const currentIndex = useMemo(() => {
    if (!plansDetails || typeof name !== 'string') return -1;

    const normalizedName = name.trim().toLowerCase();

    return Object.values(plansDetails).findIndex(
      (plan) => plan?.struPlanName?.trim()?.toLowerCase() === normalizedName
    );
  }, [plansDetails, name]);


  // useEffect(() => {
  //   if (shopName && !name) {
  //     // only fetch if name is not already in Redux
  //     dispatch(storeLimit(shopName));
  //   }
  // }, [shopName]);

  // ✅ Early return AFTER all hooks
  if (
    (typeof planStatus === 'string' && planStatus === 'INPROGRESS') ||
    !plansDetails ||
    Object.keys(plansDetails).length === 0 ||
    isLoading
  ) {
    return <Layout />;
  }

  const plans = [
    {
      planName: plansDetails?.[1]?.struPlanName,
      price: plansDetails?.[1]?.price,
      currentPlan: false,
      storeLimit: plansDetails?.[1]?.storeLimit,
      features: [
        { name: 'Up to 25,000 SKUs', available: true },
        { name: 'Sync a pre-existing catalog', available: true },
        { name: 'Sync 1- and 2-level products', available: true },
        { name: 'Sync 3-level products', available: true },
        { name: 'Sync collections', available: true },
        { name: 'Sync images & videos', available: true },
        { name: 'Sync variant options', available: true },
        { name: 'Sync standard fields', available: true },
        { name: 'Sync product & variant metafields', available: true },
        { name: 'Sync publishing channels', available: true },
        { name: 'Sync inventory tracking flag', available: true },
        { name: 'Sync scheduler', available: true },
        { name: 'Multi store management', available: true },
        { name: 'Sync Shopify defined categories*', available: true },
        { name: 'Sync Shopify defined category metafields*', available: true },
        { name: 'Auto-assign parents & grand parents collections*', available: true },
        { name: 'Generate category hierarchy as collection metafield*', available: true },
        { name: 'Sync YMMs records*', available: true },
        { name: 'Generate virtual SKUs*', available: true },
      ],
    },
    {
      planName: plansDetails?.[2]?.struPlanName,
      price: plansDetails?.[2]?.price,
      currentPlan: false,
      storeLimit: plansDetails?.[2]?.storeLimit,
      features: [
        { name: 'Up to 50,000 SKUs', available: true },
        { name: 'All Basic Features', available: true },
        { name: 'Sync localized content', available: true },
        { name: 'Sync asset collection as media', available: true },
        { name: 'Sync product & variant associations', available: true },
        { name: 'Sync reference entities', available: true },
        { name: 'Sync collection metafields', available: true },
        { name: 'Sync Shopify Markets*', available: true },
        { name: 'Sync B2B Catalog*', available: true },
        { name: 'Error notifications via Email', available: true },
        { name: 'Sync Shopify defined categories*', available: true },
        { name: 'Sync Shopify defined category metafields*', available: true },
        { name: 'Auto-assign parents & grand parents collections*', available: true },
        { name: 'Generate category hierarchy as collection metafield*', available: true },
        { name: 'Sync YMMs records*', available: true },
        { name: 'Generate virtual SKUs*', available: true },
      ],
    },
    {
      planName: plansDetails?.[3]?.struPlanName,
      price: plansDetails?.[3]?.price,
      currentPlan: false,
      storeLimit: plansDetails?.[3]?.storeLimit,
      features: [
        { name: 'Up to 100,000 SKUs', available: true },
        { name: 'All Pro Features', available: true },
        { name: 'Sync Metaobjects', available: true },
        { name: 'Sync enriched collections', available: true },
        { name: 'Configurable metafield namespaces', available: true },
        { name: 'Metafield name/value transformation', available: true },
        { name: 'Error notifications via Teams, Slack etc.', available: true },
        { name: 'Sync Shopify defined categories*', available: true },
        { name: 'Sync Shopify defined category metafields*', available: true },
        { name: 'Auto-assign parents & grand parents collections*', available: true },
        { name: 'Generate category hierarchy as collection metafield*', available: true },
        { name: 'Sync YMMs records*', available: true },
        { name: 'Generate virtual SKUs*', available: true },
        { name: 'High-performance syncs', available: true },
      ],
    },
  ];

  const renderFeatureText = (text, boldText) => {
    if (!boldText) return text;
    const parts = text.split('{skuCount}');
    return (
      <>
        {parts[0]}
        <strong>{boldText}</strong>
        {parts[1]}
      </>
    );
  };

  const onClick = async ({ planName, price, storeLimit }) => {
    if (isShopifyApp) {
      const host = new URLSearchParams(window.location.search).get('host');
      const payload = {
        planName: planName,
        price: price.toString(),
        storeDomain: shopName,
        storeLimit: storeLimit.toString(),
        test:`${process.env.NEXT_PUBLIC_SHOPIFY_CHARGE}`
      };
      dispatch(registerSubscription(payload)).then((response) => {
        const data = response?.payload;

        const shopifyRecurringSubscriptionStatus =
          data?.data?.data?.shopifyRecurringSubscriptionStatus;

        if (shopifyRecurringSubscriptionStatus === 'FAILED') {
        } else {
          const returnURL =
            data?.data?.data?.shopifyRecurringSubscriptionData
              ?.recurring_application_charge?.confirmation_url;
          if (returnURL) {
            window.open(returnURL, '_top');
          }
        }
      });
    }
  };

  return (
    <div className="row">
      {plans.map((plan, index) => (
        <div
          key={plan.planName}
          className={`col-md-4 mb-4 d-flex ${plan.currentPlan ? 'active' : ''}`}
        >
          <div
            className={`py-4 px-5 rounded-4 shadow PriceBox h-100 w-100 d-flex flex-column ${
              plan.currentPlan ? 'border-primary' : ''
            }`}
          >
            <div className="text-center">
              <h1
                className="h3 text-uppercase mb-2 fw-bold"
                style={{ letterSpacing: '5.6px' }}
              >
                {plan.planName}
              </h1>

              <h2 className="h1 fw-bold">
                ${plan.price.toFixed(2)}
                <span className="fs-6 font-weight-normal ms-2"> /MO</span>
              </h2>

              <div
                className="my-4 mx-auto"
                style={{
                  width: '80px',
                  height: '6px',
                  backgroundColor: '#c9c9c9',
                  borderRadius: '16px',
                }}
              />
            </div>

            {/* Middle Content */}
            <ul
              className="list-unstyled my-5 text-small text-left flex-grow-1"
              style={{ fontSize: '14.4px' }}
            >
              {plan.features.map((feature, index) => (
                <li key={index} className="mb-3">
                  <Image
                    src={feature.available ? CheckCircleIcon : CancelIcon}
                    alt={feature.available ? 'Available' : 'Not available'}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span className={`ms-2 ${feature.available ? '' : 'text-muted'}`}>
                    {renderFeatureText(feature.name, feature.boldText)}
                  </span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="SBtn w-100 mt-auto pt-3">
              <Button
                variant={
                  !subscriptionExpired
                    ? plan.planName?.toLowerCase() === name?.toLowerCase()
                      ? 'secondary'
                      : 'primary'
                    : 'primary'
                }
                className="stru-btn rounded-pill w-100 py-2"
                onClick={() =>
                  onClick({
                    planName: plan.planName,
                    price: plan.price,
                    storeLimit: plan.storeLimit,
                  })
                }
                disabled={!subscriptionExpired ? index < currentIndex : false}
              >
                {subscriptionPlan === plan.planName && subscriptionStatus === 'loading' && (
                  <div
                    className="spinner-border me-2"
                    role="status"
                    style={{ width: '18px', height: '18px' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
                {!subscriptionExpired
                  ? plan.planName?.toLowerCase() === name?.toLowerCase()
                    ? 'Activated'
                    : 'Subscribe'
                  : 'Subscribe'}
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Subscription;